import { Col, Row } from "react-grid-system";
import { Document, Page } from "react-pdf";
import styled from "styled-components";
import {
  DragDropContext,
  Droppable,
  Draggable,
  type DropResult,
} from "@hello-pangea/dnd";
import { theme } from "../../../theme";
import { useFile } from "../../../graphql/hooks/useFile";
import Subtitle from "../../atoms/typography/Subtitle";
import { HorizontalSpacer, VerticalSpacer } from "../../atoms/Spacers";
import Body from "../../atoms/typography/Body";
import Checkbox from "../../atoms/inputs/Checkbox";
import TextLinkButton from "../../atoms/buttons/TextLinkButton";
import { FileIconOne } from "../../atoms/icons/FileIcons";
import { DragDropVerticalIcon } from "../../atoms/icons/DragDropVerticalIcon";
import BodySmall from "../../atoms/typography/BodySmall";
import DefaultButton from "../../atoms/buttons/DefaultButton";
import { CrossIcon } from "../../atoms/icons/CrossIcon";
import LoadingSpinner from "../../atoms/LoadingSpinner";
import ActionButton from "../../atoms/buttons/ActionButton";
import { MergeIcon } from "../../atoms/icons/MergeIcon";
import { useMergeInvoiceUploadDocuments } from "../../../graphql/hooks/documentHandler/useMergeInvoiceUploadDocuments";
import Alert from "../Alert";
import ChevronButton from "../../atoms/buttons/ChevronButtons";
import { useEffect, useMemo, useState, type FC } from "react";
import { pdfjs } from "react-pdf";
import OutlinedButton from "../../atoms/buttons/OutlinedButton";
import {
  ChevronLeftIconLarge,
  ChevronRightIconLarge,
} from "../../atoms/icons/ChevronIcons";
import { useTranslation } from "react-i18next";
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface Props {
  files: Array<{ fileName: string; fileId: string; documentId: string }>;
  uploadUnitId: string;
  onClose: () => void;
}

const MergeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  border-radius: 4px;
  background-color: ${theme.colors.info05};
  padding: 8px;
  width: auto;
`;

const MergeFilesTogetherModalContent: FC<Props> = ({
  files,
  uploadUnitId,
  onClose,
}) => {
  const { t } = useTranslation();
  const [selectedFileId, setSelectedFileId] = useState<string>(
    files[0]?.fileId ?? "",
  );
  const [filesInOrder, setFilesInOrder] =
    useState<Array<{ fileName: string; fileId: string; documentId: string }>>(
      files,
    );
  const [successMessage, setsuccessMessage] = useState("");
  const [numPages, setNumPages] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [errorMessage, setErrorMessage] = useState<undefined | string>(
    undefined,
  );
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  const selectedFile = files.find((f) => f.fileId === selectedFileId);
  const { file, error, loading } = useFile(selectedFileId);

  const onClickFile = (fileId: string) => {
    setSelectedFileId(fileId);
  };

  function base64ToUint8Array(base64: string): Uint8Array {
    const binaryString = window.atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  }

  useEffect(() => {
    if (file?.content && file?.contentType === "application/pdf") {
      try {
        // Some APIs include "data:application/pdf;base64,..." prefix → strip it
        const base64 = file.content.split(",").pop() || file.content;
        const byteArray = base64ToUint8Array(base64);
        const blob = new Blob([byteArray], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);

        setPdfUrl(url);

        return () => {
          URL.revokeObjectURL(url);
        };
      } catch (error) {
        console.error("Failed to decode Base64 PDF content:", error);
        setPdfUrl(null);
      }
    } else {
      setPdfUrl(null);
    }
  }, [file]);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setCurrentPage(1);
  };

  const onClickMoveDocumentUp = (index: number) => {
    if (index === 0) return;
    const newFiles = [...filesInOrder];
    const temp = newFiles[index - 1];
    newFiles[index - 1] = newFiles[index];
    newFiles[index] = temp;
    setFilesInOrder(newFiles);
  };

  const onClickMoveDocumentDown = (index: number) => {
    if (index === filesInOrder.length - 1) return;
    const newFiles = [...filesInOrder];
    const temp = newFiles[index + 1];
    newFiles[index + 1] = newFiles[index];
    newFiles[index] = temp;
    setFilesInOrder(newFiles);
  };

  const onCompletedMerge = (result: any) => {
    if (
      result?.documentHandler?.mergeInvoiceUploadDocuments?.success === true
    ) {
      setsuccessMessage(t("documentHandler.theFilesHaveBeenMerged"));
      setErrorMessage(undefined);
    } else {
      setErrorMessage(t("documentHandler.unableToMergeTheFiles"));
    }
  };
  const onErrorMerge = () => {
    setErrorMessage(t("documentHandler.unableToMergeTheFiles"));
  };
  const {
    mergeInvoiceUploadDocuments,
    error: mergeError,
    loading: mergeLoading,
  } = useMergeInvoiceUploadDocuments({
    onCompleted: onCompletedMerge,
    onError: onErrorMerge,
  });

  const onClickMergeFiles = async () => {
    try {
      const documentIds = filesInOrder.map((f) => f.documentId);
      await mergeInvoiceUploadDocuments({
        variables: {
          input: {
            uploadUnitId: uploadUnitId,
            uploadDocumentIds: documentIds,
          },
        },
      });
    } catch (e) {
      setErrorMessage(t("documentHandler.unableToMergeTheFiles") + e);
    }
  };

  const onToggleFileInclusion = (fileId: string) => {
    const file = files.find((f) => f.fileId === fileId);
    if (!file) return;

    const fileIsIncluded = filesInOrder.some((f) => f.fileId === fileId);
    if (fileIsIncluded) {
      const newFiles = filesInOrder.filter((f) => f.fileId !== fileId);
      setFilesInOrder(newFiles);
    } else {
      setFilesInOrder([...filesInOrder, file]);
    }
  };

  // Drag & drop
  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const items = Array.from(filesInOrder);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    setFilesInOrder(items);
  };

  const memoizedFile = useMemo(() => pdfUrl, [pdfUrl]);

  return (
    <div>
      <Row justify="center">
        <Subtitle color={theme.colors.primary}>
          {t("documentHandler.mergeFiles")}
        </Subtitle>
      </Row>

      <HorizontalSpacer spacing={1} />
      <Row>
        <Col lg={2}>
          <Body fontweight="700">
            {files.length} {t("documentHandler.document").toLowerCase()}
          </Body>
          <HorizontalSpacer spacing={3} />
          {files.map((file) => (
            <div key={file.fileId} style={{ display: "flex", marginTop: 16 }}>
              <Checkbox
                checked={filesInOrder.some((f) => f.fileId === file.fileId)}
                onChange={() => onToggleFileInclusion(file.fileId)}
              />{" "}
              <TextLinkButton
                title={file.fileName}
                icon={
                  <FileIconOne
                    color={
                      selectedFile === file
                        ? theme.colors.primary
                        : theme.colors.textSecondary
                    }
                  />
                }
                color={
                  selectedFile === file
                    ? theme.colors.primary
                    : theme.colors.textSecondary
                }
                onClick={() => onClickFile(file.fileId)}
              />
            </div>
          ))}
        </Col>

        <Col lg={5}>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="files">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {filesInOrder.map((f, index) => (
                    <Draggable
                      key={f.fileId}
                      draggableId={f.fileId}
                      index={index}
                    >
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          style={{ ...provided.draggableProps.style }}
                        >
                          <Row
                            justify="between"
                            align="center"
                            style={{ margin: 8 }}
                          >
                            <Col lg={1}>
                              <Body>{index + 1}</Body>
                            </Col>
                            <Col lg={7}>
                              <MergeRow {...provided.dragHandleProps}>
                                <DragDropVerticalIcon />
                                <BodySmall>{f.fileName}</BodySmall>
                              </MergeRow>
                            </Col>
                            <Col lg={3}>
                              <div style={{ display: "flex", gap: "12px" }}>
                                <ChevronButton
                                  onClick={() => onClickMoveDocumentUp(index)}
                                  type="up"
                                />
                                <ChevronButton
                                  onClick={() => onClickMoveDocumentDown(index)}
                                  type="down"
                                />
                              </div>
                            </Col>
                          </Row>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          <HorizontalSpacer spacing={2} />
          {successMessage && (
            <div>
              <Alert type="success" title={successMessage} />
              <HorizontalSpacer spacing={2} />
            </div>
          )}
          <Row justify="end" nogutter>
            <DefaultButton
              title={t("common.cancel")}
              icon={<CrossIcon color={theme.colors.text} />}
              onClick={onClose}
            />
            <VerticalSpacer spacing={1.5} />
            <ActionButton
              title={t("documentHandler.mergeFiles")}
              icon={<MergeIcon color="white" />}
              onClick={onClickMergeFiles}
              color={theme.colors.primary}
              disabled={successMessage !== ""}
              loading={mergeLoading}
            />
          </Row>
        </Col>

        <Col lg={5}>
          {(error || mergeError) && (
            <Alert
              type="error"
              title={errorMessage ?? t("errors.somethingWentWrong")}
              message={error?.toString() ?? mergeError?.toString()}
            />
          )}
          {loading && <LoadingSpinner />}
          {pdfUrl && (
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              {numPages && numPages > 1 && currentPage > 1 && (
                <OutlinedButton
                  icon={
                    <ChevronLeftIconLarge
                      color={
                        currentPage <= 1
                          ? theme.colors.white
                          : theme.colors.text
                      }
                    />
                  }
                  title={t("documentHandler.previousPage")}
                  disabled={currentPage <= 1}
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  color={theme.colors.text}
                />
              )}
              <Body>
                {t("common.page")} {currentPage} av {numPages}
              </Body>
              {numPages && numPages > 1 && (
                <OutlinedButton
                  iconRight={
                    <ChevronRightIconLarge
                      color={
                        currentPage >= (numPages ?? 1)
                          ? theme.colors.white
                          : theme.colors.text
                      }
                    />
                  }
                  disabled={numPages ? currentPage >= numPages : true}
                  title={t("documentHandler.nextPage")}
                  color={theme.colors.text}
                  onClick={() =>
                    setCurrentPage((prev) =>
                      numPages ? Math.min(prev + 1, numPages) : prev + 1,
                    )
                  }
                />
              )}
            </div>
          )}
          {pdfUrl && (
            <>
              <Document
                key={selectedFileId}
                file={memoizedFile}
                onLoadError={(err) => console.error("PDF load error:", err)}
                onLoadSuccess={onDocumentLoadSuccess}
              >
                <Page pageNumber={currentPage} />
              </Document>
            </>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default MergeFilesTogetherModalContent;
